import { Lightning, Warning, Baby } from "@phosphor-icons/react";
import { isCaesariaCandidate } from "../lib/format";
import SearchableSelect from "./SearchableSelect";
import BiayaTambahanEditor from "./BiayaTambahanEditor";

const MULTIPLET_OPTS = ["GEMELLI", "TRIPLET", "QUADRUPLET"];

export default function TindakanCard({
  index,
  data,
  onChange,
  kategoriList,
}) {
  const isCaesaria = isCaesariaCandidate(data.kategori);
  const update = (patch) => onChange({ ...data, ...patch });
  return (
    <div
      className="bg-white border border-slate-200 rounded shadow-sm p-5 md:p-6"
      data-testid={`tindakan-card-${index + 1}`}
    >
      <div className="pb-4 mb-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Tindakan {index + 1}
          </div>
          <div className="text-base md:text-lg font-semibold text-slate-900">
            {index === 0
              ? "Tindakan Utama"
              : index === 1
                ? "Tindakan Tambahan ke-2"
                : "Tindakan Tambahan ke-3"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
            Nama Tindakan
          </label>
          <input
            type="text"
            value={data.nama_tindakan || ""}
            onChange={(e) => update({ nama_tindakan: e.target.value })}
            placeholder="Contoh: Apendektomi"
            className="w-full bg-white border border-slate-300 text-slate-900 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A] focus:border-[#0A3F9A] transition-all"
            data-testid={`input-nama-tindakan-${index + 1}`}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
            Kategori Tindakan
          </label>
          <SearchableSelect
            value={data.kategori || ""}
            onChange={(v) => update({ kategori: v, multiplet: null })}
            options={kategoriList.map((k) => ({
              value: k,
              label: `Operasi ${k}`,
            }))}
            placeholder="Cari kategori tindakan..."
            testid={`select-kategori-${index + 1}`}
          />
        </div>

        {index > 0 && (
          <>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Lokasi (vs Tindakan ke-1)
              </label>
              <select
                value={data.lokasi || "BEDA"}
                onChange={(e) => update({ lokasi: e.target.value })}
                className="w-full bg-white border border-slate-300 text-slate-900 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid={`select-lokasi-${index + 1}`}
              >
                <option value="SAMA">Lokasi SAMA</option>
                <option value="BEDA">Lokasi BEDA</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Operator (vs Tindakan ke-1)
              </label>
              <select
                value={data.operator || "SAMA"}
                onChange={(e) => update({ operator: e.target.value })}
                className="w-full bg-white border border-slate-300 text-slate-900 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid={`select-operator-${index + 1}`}
              >
                <option value="SAMA">Operator SAMA</option>
                <option value="BEDA">Operator BEDA</option>
              </select>
            </div>
          </>
        )}
      </div>

      <div className="mt-5">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">
          Kondisi Khusus
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label
            className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
              data.cito
                ? "border-[#DC2626] bg-[#FEF2F2]"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`toggle-cito-${index + 1}`}
          >
            <input
              type="checkbox"
              checked={!!data.cito}
              onChange={(e) => update({ cito: e.target.checked })}
              className="mt-0.5 accent-[#DC2626]"
            />
            <div>
              <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
                <Lightning size={14} weight="fill" className="text-[#DC2626]" /> CITO
              </div>
              <div className="text-xs text-slate-600">
                Tindakan darurat (+30% dokter, +15% fasilitas)
              </div>
            </div>
          </label>
          <label
            className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
              data.penyulit
                ? "border-[#D97706] bg-[#FFFBEB]"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`toggle-penyulit-${index + 1}`}
          >
            <input
              type="checkbox"
              checked={!!data.penyulit}
              onChange={(e) => update({ penyulit: e.target.checked })}
              className="mt-0.5 accent-[#D97706]"
            />
            <div>
              <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
                <Warning size={14} weight="fill" className="text-[#D97706]" /> PENYULIT
              </div>
              <div className="text-xs text-slate-600">
                Komplikasi (+30% dokter, +15% fasilitas)
              </div>
            </div>
          </label>
        </div>

        {isCaesaria && (
          <div className="mt-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2 flex items-center gap-1.5">
              <Baby size={14} weight="duotone" /> Multiplet (untuk Caesaria/Kebidanan)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => update({ multiplet: null })}
                className={`px-3 py-2 text-xs font-bold uppercase rounded border transition-colors ${
                  !data.multiplet
                    ? "bg-[#0A3F9A] text-white border-[#0A3F9A]"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid={`btn-multiplet-none-${index + 1}`}
              >
                Tunggal
              </button>
              {MULTIPLET_OPTS.map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => update({ multiplet: m })}
                  className={`px-3 py-2 text-xs font-bold uppercase rounded border transition-colors ${
                    data.multiplet === m
                      ? "bg-[#0A3F9A] text-white border-[#0A3F9A]"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid={`btn-multiplet-${m.toLowerCase()}-${index + 1}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col gap-3">
          <label
            className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors max-w-md ${
              data.include_asisten === false
                ? "border-slate-200 bg-slate-50"
                : "border-[#0A3F9A] bg-blue-50"
            }`}
            data-testid={`toggle-asisten-${index + 1}`}
          >
            <input
              type="checkbox"
              checked={data.include_asisten !== false}
              onChange={(e) => update({ include_asisten: e.target.checked })}
              className="mt-0.5 accent-[#0A3F9A]"
            />
            <div>
              <div className="text-sm font-bold text-slate-900">
                Hitung Jasa Asisten Operator
              </div>
              <div className="text-xs text-slate-600">
                Hilangkan centang jika tindakan tidak memerlukan Jasa Asisten / Dokter
                Asisten.
              </div>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors max-w-md ${
              data.include_sewa_alat_khusus === false
                ? "border-slate-200 bg-slate-50"
                : "border-[#0A3F9A] bg-blue-50"
            }`}
            data-testid={`toggle-sewa-alat-khusus-${index + 1}`}
          >
            <input
              type="checkbox"
              checked={data.include_sewa_alat_khusus !== false}
              onChange={(e) =>
                update({ include_sewa_alat_khusus: e.target.checked })
              }
              className="mt-0.5 accent-[#0A3F9A]"
            />
            <div>
              <div className="text-sm font-bold text-slate-900">
                Hitung Sewa Alat Khusus
              </div>
              <div className="text-xs text-slate-600">
                Hilangkan centang jika tindakan tidak memerlukan Sewa Alat Khusus.
              </div>
            </div>
          </label>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-1.5 block">
              Tarif Manajemen Nyeri (Rp)
            </label>
            <input
              type="number"
              min={0}
              step={50000}
              value={data.manajemen_nyeri ?? 0}
              onChange={(e) =>
                update({ manajemen_nyeri: Number(e.target.value) || 0 })
              }
              placeholder="0"
              className="w-full bg-white border border-slate-300 text-slate-900 rounded px-3 py-2 text-sm tabular-nums focus:ring-2 focus:ring-[#0A3F9A]"
              data-testid={`input-manajemen-nyeri-${index + 1}`}
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Biaya tambahan per tindakan. Kosongkan / 0 bila tidak ada.
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-1.5 block">
              Obat &amp; BHP OK (Rp)
            </label>
            <input
              type="number"
              min={0}
              step={50000}
              value={data.obat_bhp_ok ?? 0}
              onChange={(e) =>
                update({ obat_bhp_ok: Number(e.target.value) || 0 })
              }
              placeholder="0"
              className="w-full bg-white border border-slate-300 text-slate-900 rounded px-3 py-2 text-sm tabular-nums focus:ring-2 focus:ring-[#0A3F9A]"
              data-testid={`input-obat-bhp-${index + 1}`}
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Obat &amp; bahan habis pakai di kamar operasi.
            </div>
          </div>
        </div>

        <BiayaTambahanEditor
          index={index}
          items={data.biaya_tambahan || []}
          onChange={(list) => update({ biaya_tambahan: list })}
        />
      </div>
    </div>
  );
}
